import { NavNotification } from '../components/nav-notification';
import { UserInfo } from '../components/use-info';
import { InteractLeaderboardIcon, ViewRewardsIcon } from '@/client/assets';
import { AuthedButton } from '@/client/components/authed-button';
import { SearchButton } from '@/client/components/search/search-button';
import { SwitchBaseToken } from '@/client/components/switch-base-token';
import { SwitchLocale } from '@/client/components/switch-locale';
import { Button } from '@/client/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import { NOTIFICATIONS_ENABLED } from '@/client/confs/app';
import { prefetchRoute } from '@/client/confs/page-loaders';
import { useRewardsEntry } from '@/client/hooks/use-rewards-entry';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { AppTitle } from '@/client/layouts/components/app-title';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { getScrollBehavior } from '@/client/utils/dom';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useRoute } from 'wouter';

export const TheMarketHeader = () => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { data: signer } = useSignedUserInfo(api);
  const rewardsEntry = useRewardsEntry();
  const portfolioLink = useMemo(() => `/user/${signer?.uid}`, [signer?.uid]);
  const [isLeaderboardActive] = useRoute('/leaderboard');
  const [isRewardsActive] = useRoute(rewardsEntry?.activePattern ?? '/user/:uid/invite-earn');
  // /rewards opens signed out; the legacy per-uid page asks for sign-in first.
  const RewardsButton = rewardsEntry?.requiresAuth ? AuthedButton : Button;
  const rewardsAuthProps = rewardsEntry?.requiresAuth ? { showAuth: false, showLoading: false } : {};
  const [isHomeActive] = useRoute('/');
  const [isUserRoute, userRouteParams] = useRoute('/user/:id');
  const isPortfolioActive = isUserRoute && userRouteParams?.id === signer?.uid;

  // DPIN has no standalone /markets route (unlike AIP): from the home page
  // this scrolls straight to the browse list (same treatment as "View all"
  // in trending-now/index.tsx); from any other page it navigates home first
  // and flags the target via a query param that home/index.tsx consumes on
  // mount, so the header link never dead-ends off the home page.
  const handleMarketsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isHomeActive) {
      document.querySelector('#market-list')?.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
    } else {
      navigate('/?scrollTo=market-list');
    }
  };

  return (
    <header
      className={cn(
        'w-full border-b border-foreground/4 bg-background/95 backdrop-blur-md h-13',
        'md:(h-15 pos-sticky top-0 z-50)'
      )}
    >
      <div className="w-full flex items-center justify-between size-full mx-auto max-w-[1400px] px-4 md:px-6 gap-4">
        {/* Left side: Logo + nav */}
        <div className="flex items-center justify-start gap-3 flex-1 min-w-0">
          <AppTitle />
          {/* lg, not md: at 768 the pills collide with the 320px search box */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link href="/" className={cn('dpin-nav-link', isHomeActive && 'active')}>
              {t('home', 'Home')}
            </Link>
            <Link href="/" className="dpin-nav-link" onClick={handleMarketsClick}>
              {t('markets', 'Markets')}
            </Link>
            <AuthedButton
              variant="ghost"
              showAuth={false}
              showLoading={false}
              onClick={() => navigate(portfolioLink)}
              onMouseEnter={() => prefetchRoute('user')}
              onFocus={() => prefetchRoute('user')}
              onTouchStart={() => prefetchRoute('user')}
              className={cn('dpin-nav-link', isPortfolioActive && 'active')}
            >
              {t('portfolio', 'Portfolio')}
            </AuthedButton>
          </nav>
          <div className="hidden md:block">
            <SwitchBaseToken />
          </div>
        </div>

        {/* Center: Search */}
        <div className="hidden min-w-0 md:flex md:w-[320px]">
          <SearchButton />
        </div>

        {/* Right side: Nav + Actions */}
        <div className="flex items-center justify-end gap-2 md:gap-3 flex-1">
          {/* Mobile: currency lens + language switch get the header slot
                (7/23, currency added 9/4). The old search + hamburger pair
                duplicated NavMobile's bottom bar; the wallet drawer's DPS▾
                buries the lens two taps deep (open drawer, then the card),
                so it surfaces here too — `compact` self-hides above md via
                the same useMedia check as SwitchLocale's `showOnMobile`, so
                it needs no `hidden md:block` wrapper of its own. Accent color
                for the locale trigger comes from .dpin-locale-trigger (gold
                in dark, deep amber in light). */}
          <SwitchBaseToken compact />
          <SwitchLocale showOnMobile className="md:hidden dpin-locale-trigger" />
          {/* Leaderboard */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/leaderboard"
                onMouseEnter={() => prefetchRoute('leaderboard')}
                onFocus={() => prefetchRoute('leaderboard')}
                onTouchStart={() => prefetchRoute('leaderboard')}
                className={cn(
                  'hidden md:flex items-center justify-center size-9 rounded-md cursor-pointer transition-colors border border-foreground/4',
                  isLeaderboardActive ? 'bg-foreground/5' : 'bg-transparent hover:bg-foreground/5'
                )}
              >
                <InteractLeaderboardIcon className="size-5 fill-foreground" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>{t('leaderboard')}</TooltipContent>
          </Tooltip>

          {/* Rewards */}
          {rewardsEntry && (
            <Tooltip>
              <TooltipTrigger asChild>
                <RewardsButton
                  {...rewardsAuthProps}
                  onClick={() => navigate(rewardsEntry.href)}
                  onMouseEnter={() => prefetchRoute(rewardsEntry.prefetch)}
                  onFocus={() => prefetchRoute(rewardsEntry.prefetch)}
                  onTouchStart={() => prefetchRoute(rewardsEntry.prefetch)}
                  variant="outline"
                  size="icon"
                  aria-label={t('rewards')}
                  className={cn(
                    'hidden md:flex p-2 border border-foreground/4 shadow-none',
                    isRewardsActive ? 'bg-foreground/5' : 'bg-transparent hover:bg-foreground/5'
                  )}
                >
                  <ViewRewardsIcon className="size-5 fill-primary" />
                </RewardsButton>
              </TooltipTrigger>
              <TooltipContent>{t('rewards')}</TooltipContent>
            </Tooltip>
          )}

          {/* Notifications off (BON-249): no bell. */}
          {NOTIFICATIONS_ENABLED && <NavNotification />}
          <UserInfo />
        </div>
      </div>
    </header>
  );
};
